import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, MessageSquare } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import SearchableSelect from '@/components/common/SearchableSelect';
import SupportThread, { SUPPORT_POLL_MS, when, type SupportConversationRow } from '@/components/global-admin/SupportThread';
import { authenticatedBitswanBackendInstance } from '@/lib/api-client';

type Board = {
  conversations: SupportConversationRow[];
  participants: { organizations: Array<{ id: string; name: string }>; agents: string[] };
  me: string;
};

export default function MessagesPage() {
  const [q, setQ] = React.useState('');
  const [organization, setOrganization] = React.useState('');
  const [agent, setAgent] = React.useState('');
  const [selected, setSelected] = React.useState<string | null>(null);

  const board = useQuery({
    queryKey: ['support-board', q, organization, agent],
    queryFn: async () => {
      const api = await authenticatedBitswanBackendInstance();
      const res = await api.get('/global-admin/support/conversations/', { params: { q, organization, agent } });
      return res.data as Board;
    },
    refetchInterval: SUPPORT_POLL_MS,
  });

  const rows = board.data?.conversations ?? [];
  const mine = rows.filter((row) => row.mine);
  const others = rows.filter((row) => !row.mine);
  React.useEffect(() => {
    if (!selected && rows.length > 0) setSelected(rows[0].id);
  }, [rows, selected]);

  const Row = ({ row }: { row: SupportConversationRow }) => (
    <button
      type="button"
      onClick={() => setSelected(row.id)}
      className={`flex w-full items-start gap-3 rounded-md px-3 py-2.5 text-left hover:bg-muted ${selected === row.id ? 'bg-muted' : ''}`}
      data-testid={`support-conversation-${row.id}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-sm font-medium">{row.organization_name || row.organization_id}</p>
          <Badge variant="outline" className="h-5 text-[10px] font-normal">{row.organization_kind === 'advisor' ? 'advisor' : 'customer'}</Badge>
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {row.last_message_side === 'platform' ? 'You: ' : ''}{row.last_message_preview || 'No messages yet'}
        </p>
        <p className="text-[11px] text-muted-foreground">
          {when(row.last_message_at)}{row.assigned_to_email ? ` · ${row.assigned_to_email}` : ' · unanswered'}
        </p>
      </div>
      {row.unread > 0 && <Badge className="h-5 min-w-[20px] justify-center px-1.5 text-[10px]" data-testid="support-unread">{row.unread}</Badge>}
    </button>
  );

  return (
    <div className="space-y-4 p-6" data-testid="messages-page">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Messages</h1>
        <p className="text-muted-foreground">Every conversation between support and a customer or advisor. Yours come first.</p>
      </div>

      <div className="flex flex-wrap gap-2">
        <Input placeholder="Search people and organisations…" value={q} onChange={(e) => setQ(e.target.value)} className="w-64" data-testid="support-search" />
        <SearchableSelect
          className="h-9 w-auto min-w-[12rem]"
          value={organization}
          onChange={setOrganization}
          placeholder="Any organisation"
          searchPlaceholder="Search organisations…"
          options={[{ value: '', label: 'Any organisation' }, ...(board.data?.participants.organizations ?? []).map((o) => ({ value: o.id, label: o.name }))]}
          testId="support-filter-organization"
        />
        <SearchableSelect
          className="h-9 w-auto min-w-[12rem]"
          value={agent}
          onChange={setAgent}
          placeholder="Any agent"
          searchPlaceholder="Search agents…"
          options={[{ value: '', label: 'Any agent' }, ...(board.data?.participants.agents ?? []).map((a) => ({ value: a, label: a === board.data?.me ? `${a} (you)` : a }))]}
          testId="support-filter-agent"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <div className="max-h-[calc(100vh-16rem)] overflow-y-auto rounded-xl border bg-card p-2" data-testid="support-list">
          {board.isLoading ? (
            <div className="flex justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-muted-foreground" /></div>
          ) : rows.length === 0 ? (
            <p className="py-12 text-center text-sm text-muted-foreground" data-testid="support-empty">No conversations match.</p>
          ) : (
            <>
              {mine.length > 0 && (
                <>
                  <p className="px-3 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Yours</p>
                  {mine.map((row) => <Row key={row.id} row={row} />)}
                  {others.length > 0 && <p className="px-3 pb-1 pt-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Everyone else's</p>}
                </>
              )}
              {others.map((row) => <Row key={row.id} row={row} />)}
            </>
          )}
        </div>
        <div className="h-[calc(100vh-16rem)] overflow-hidden rounded-xl border bg-card">
          {selected ? (
            <SupportThread key={selected} conversationId={selected} />
          ) : (
            <div className="flex h-full flex-col items-center justify-center gap-2 text-muted-foreground">
              <MessageSquare className="h-6 w-6" />
              <p className="text-sm">Pick a conversation.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
